import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ArrowDownward as ArrowDownIcon,
  ArrowUpward as ArrowUpIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  EmailOutlined as EmailIcon,
  OpenInNew as PreviewIcon,
  PlaylistAddCheck as ShortlistIcon,
  Textsms as TextsmsIcon,
} from "@mui/icons-material";
import {
  addClientShortlistItem,
  buildPublicShortlistUrl,
  getClientShortlistDetail,
  getOrCreateClientShortlist,
  reorderClientShortlistItems,
  removeClientShortlistItem,
  updateClientShortlistItem,
  type ClientShortlistDetail,
  type ClientShortlistItem,
  type ClientShortlistProperty,
} from "../../services/clientShortlistService";
import {
  confirmShortlistSmsSent,
  handoffShortlistToNativeSms,
  type ShortlistSmsHandoffReceipt,
} from "../../services/shortlistSmsHandoff";
import { TOUCH_TARGET } from "../../theme/theme";

export interface ShortlistSelection {
  propertyId: string;
  propertyName: string;
  neighborhood?: string;
  imageUrl?: string;
  minRent?: number;
  maxRent?: number;
  floorPlanIds: string[];
  floorPlanNames: string[];
  selectedUnitKeys: string[];
}

interface ShortlistClient {
  id: string;
  first_name: string;
  last_name: string;
  phone?: string | null;
  email?: string | null;
}

interface ShortlistComposerProps {
  client: ShortlistClient | null;
  selections: ShortlistSelection[];
  onEmail: () => void;
  onNotice: (message: string, severity: "success" | "error" | "info") => void;
}

function formatRent(
  item?: ClientShortlistItem,
  property?: ClientShortlistProperty
): string {
  const selectedMinRent = item?.metadata?.selected_min_rent;
  const selectedMaxRent = item?.metadata?.selected_max_rent;
  const minRent =
    typeof selectedMinRent === "number" && Number.isFinite(selectedMinRent)
      ? selectedMinRent
      : property?.min_rent;
  const maxRent =
    typeof selectedMaxRent === "number" && Number.isFinite(selectedMaxRent)
      ? selectedMaxRent
      : property?.max_rent;

  if (!property && minRent === undefined && maxRent === undefined) {
    return "Rent not listed";
  }
  if (minRent && maxRent && minRent !== maxRent) {
    return `$${minRent.toLocaleString()}–$${maxRent.toLocaleString()}`;
  }
  if (minRent) return `From $${minRent.toLocaleString()}`;
  if (maxRent) return `Up to $${maxRent.toLocaleString()}`;
  return "Rent not listed";
}

function selectedFloorPlanLabel(item: ClientShortlistItem): string | null {
  const names = item.metadata?.floor_plan_names;
  if (!Array.isArray(names)) return null;
  const cleanNames = names.filter(
    (name): name is string => typeof name === "string" && name.trim().length > 0
  );
  return cleanNames.length > 0 ? cleanNames.join(", ") : null;
}

const ClientShortlistComposer: React.FC<ShortlistComposerProps> = ({
  client,
  selections,
  onEmail,
  onNotice,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<ClientShortlistDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingSmsConfirmation, setPendingSmsConfirmation] =
    useState<ShortlistSmsHandoffReceipt | null>(null);
  const [confirmingSms, setConfirmingSms] = useState(false);

  const activeItems = useMemo(
    () =>
      (detail?.items || [])
        .filter((item) => item.status === "active")
        .sort((a, b) => a.sort_order - b.sort_order),
    [detail]
  );
  const propertiesById = useMemo(
    () => new Map((detail?.properties || []).map((property) => [property.id, property])),
    [detail]
  );

  const loadShortlist = useCallback(async () => {
    if (!client) return null;
    setLoading(true);
    setError(null);
    try {
      const shortlist = await getOrCreateClientShortlist(client.id);
      const nextDetail = await getClientShortlistDetail(shortlist.id);
      setDetail(nextDetail);
      return nextDetail;
    } catch (loadError: any) {
      setError(loadError?.message || "Could not load this shortlist.");
      return null;
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => {
    setDetail(null);
    setOpen(false);
    setError(null);
    setPendingSmsConfirmation(null);
  }, [client?.id]);

  const handleAdd = async () => {
    if (!client) {
      onNotice("Select a client before building a shortlist.", "info");
      return;
    }
    if (selections.length === 0) return;

    setSaving(true);
    setError(null);
    try {
      const shortlist = await getOrCreateClientShortlist(client.id);
      const current = await getClientShortlistDetail(shortlist.id);
      const existingItemsByPropertyId = new Map(
        current.items
          .filter((item) => item.status === "active")
          .map((item) => [item.property_id, item] as const)
      );
      let sortOrder = current.items.reduce(
        (max, item) => Math.max(max, item.sort_order || 0),
        0
      );
      let added = 0;
      let updated = 0;

      for (const selection of selections) {
        const selectionMetadata = {
          source: "agent_search",
          selection_mode: selection.selectedUnitKeys.length > 0 ? "unit" : "floorplan",
          floor_plan_ids: selection.floorPlanIds,
          floor_plan_names: selection.floorPlanNames,
          selected_unit_keys: selection.selectedUnitKeys,
          selected_min_rent: selection.minRent,
          selected_max_rent: selection.maxRent,
        };
        const existingItem = existingItemsByPropertyId.get(selection.propertyId);
        if (existingItem) {
          await updateClientShortlistItem(shortlist.id, existingItem.id, {
            metadata: selectionMetadata,
          });
          updated += 1;
          continue;
        }
        sortOrder += 1;
        await addClientShortlistItem(
          shortlist.id,
          selection.propertyId,
          sortOrder,
          selectionMetadata
        );
        added += 1;
      }

      const refreshed = await getClientShortlistDetail(shortlist.id);
      setDetail(refreshed);
      setOpen(true);
      onNotice(
        added > 0 && updated > 0
          ? `${added} added and ${updated} updated on ${client.first_name}'s shortlist.`
          : added > 0
            ? `${added} ${added === 1 ? "property" : "properties"} added to ${client.first_name}'s shortlist.`
            : `${updated} ${updated === 1 ? "property" : "properties"} updated on ${client.first_name}'s shortlist.`,
        "success"
      );
    } catch (saveError: any) {
      const message = saveError?.message || "Could not update the shortlist.";
      setError(message);
      onNotice(message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (item: ClientShortlistItem) => {
    if (!detail) return;
    setSaving(true);
    try {
      await removeClientShortlistItem(detail.shortlist.id, item.id);
      await loadShortlist();
    } catch (removeError: any) {
      setError(removeError?.message || "Could not remove that property.");
    } finally {
      setSaving(false);
    }
  };

  const handleMove = async (index: number, direction: -1 | 1) => {
    if (!detail) return;
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= activeItems.length) return;
    const orderedIds = activeItems.map((item) => item.id);
    [orderedIds[index], orderedIds[swapIndex]] = [
      orderedIds[swapIndex],
      orderedIds[index],
    ];
    setSaving(true);
    try {
      await reorderClientShortlistItems(detail.shortlist.id, orderedIds);
      await loadShortlist();
    } catch (moveError: any) {
      setError(moveError?.message || "Could not reorder the shortlist.");
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    const token = detail?.shortlist.public_token;
    if (!token) return;
    window.open(buildPublicShortlistUrl(token), "_blank", "noopener,noreferrer");
  };

  const handleText = async () => {
    if (!client || !detail?.shortlist.public_token) return;
    try {
      const receipt = await handoffShortlistToNativeSms({
        client,
        shortlistId: detail.shortlist.id,
        publicUrl: buildPublicShortlistUrl(detail.shortlist.public_token),
      });
      setPendingSmsConfirmation(receipt);
    } catch (handoffError: any) {
      const message = handoffError?.message || "Could not prepare this text.";
      setError(message);
      onNotice(message, "error");
    }
  };

  const handleConfirmSmsSent = async () => {
    if (!pendingSmsConfirmation) return;
    setConfirmingSms(true);
    try {
      await confirmShortlistSmsSent(pendingSmsConfirmation);
      setPendingSmsConfirmation(null);
      onNotice(`Text to ${pendingSmsConfirmation.client.first_name} marked sent.`, "success");
    } catch (confirmationError: any) {
      const message = confirmationError?.message || "Could not mark this text sent.";
      setError(message);
      onNotice(message, "error");
    } finally {
      setConfirmingSms(false);
    }
  };

  if (selections.length === 0 && !open) return null;

  const selectionLabel = `${selections.length} ${
    selections.length === 1 ? "property" : "properties"
  } selected`;

  return (
    <>
      {selections.length > 0 && (
        <Paper
          elevation={12}
          data-tour="shortlist-tray"
          sx={{
            position: "fixed",
            zIndex: 1200,
            left: { xs: 12, sm: "auto" },
            right: { xs: 12, sm: 24 },
            bottom: {
              xs: "calc(env(safe-area-inset-bottom, 0px) + 88px)",
              sm: 24,
            },
            width: { xs: "auto", sm: 470 },
            p: 1.25,
            borderRadius: 3.5,
            border: "1px solid",
            borderColor: "divider",
            background: "linear-gradient(145deg, rgba(15,23,42,.98), rgba(30,41,59,.98))",
            color: "white",
            boxShadow: "0 18px 55px rgba(2,6,23,.45)",
            backdropFilter: "blur(18px)",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2.25,
                display: "grid",
                placeItems: "center",
                bgcolor: "rgba(59,130,246,.18)",
                color: "#93c5fd",
                flexShrink: 0,
              }}
            >
              <ShortlistIcon />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={750} noWrap>
                {client ? `${client.first_name}'s shortlist` : "Choose a client"}
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,.65)" }}>
                {selectionLabel}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="text"
              onClick={onEmail}
              aria-label="Email selected properties"
              startIcon={<EmailIcon />}
              sx={{
                color: "rgba(255,255,255,.82)",
                minWidth: { xs: TOUCH_TARGET, sm: 88 },
                minHeight: TOUCH_TARGET,
              }}
            >
              <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                Email selected
              </Box>
            </Button>
            <Button
              data-tour="add-to-shortlist"
              size="small"
              variant="contained"
              onClick={handleAdd}
              disabled={saving || !client}
              sx={{
                minHeight: 44,
                px: { xs: 1.5, sm: 2.25 },
                borderRadius: 2.25,
                textTransform: "none",
                fontWeight: 750,
                whiteSpace: "nowrap",
              }}
            >
              {saving ? <CircularProgress size={18} color="inherit" /> : `Add${client ? ` for ${client.first_name}` : ""}`}
            </Button>
          </Stack>
        </Paper>
      )}

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 480 },
            backgroundColor: "background.default",
          },
          "data-tour": "shortlist-composer",
        } as any}
      >
        <Box
          sx={{
            px: 2.25,
            pt: "calc(env(safe-area-inset-top, 0px) + 18px)",
            pb: 1.75,
            position: "sticky",
            top: 0,
            zIndex: 2,
            bgcolor: "background.paper",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <IconButton aria-label="Close shortlist" onClick={() => setOpen(false)} sx={{ minWidth: 44, minHeight: 44 }}>
              <CloseIcon />
            </IconButton>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="h6" fontWeight={800}>
                {client ? `${client.first_name}'s Shortlist` : "Shortlist"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Compare, order, and preview the client link.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<PreviewIcon />}
              disabled={!detail?.shortlist.public_token || activeItems.length === 0}
              onClick={handlePreview}
              sx={{ minHeight: 44, borderRadius: 2.25, textTransform: "none", fontWeight: 700 }}
            >
              Preview
            </Button>
          </Stack>
        </Box>

        <Box sx={{ p: 2, pb: "calc(env(safe-area-inset-bottom, 0px) + 100px)" }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {loading ? (
            <Box sx={{ py: 8, display: "grid", placeItems: "center" }}>
              <CircularProgress />
            </Box>
          ) : activeItems.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, borderRadius: 3, textAlign: "center" }}>
              <ShortlistIcon sx={{ fontSize: 44, color: "text.disabled", mb: 1 }} />
              <Typography fontWeight={700}>No properties yet</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Select properties in Search and add them here.
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={1.5} divider={<Divider flexItem />}>
              {activeItems.map((item, index) => {
                const property = propertiesById.get(item.property_id);
                return (
                  <Stack key={item.id} direction="row" spacing={1.5} alignItems="center">
                    <Box
                      component={property?.s3_image_url ? "img" : "div"}
                      src={property?.s3_image_url || undefined}
                      alt=""
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: 2.25,
                        objectFit: "cover",
                        bgcolor: "action.hover",
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography fontWeight={750} noWrap>
                        {property?.name || "Property"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {[property?.neighborhood, formatRent(item, property)].filter(Boolean).join(" · ")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[selectedFloorPlanLabel(item), `Choice ${index + 1} of ${activeItems.length}`]
                          .filter(Boolean)
                          .join(" · ")}
                      </Typography>
                    </Box>
                    <Stack direction={isMobile ? "column" : "row"} spacing={0.25}>
                      <IconButton aria-label="Move up" disabled={saving || index === 0} onClick={() => handleMove(index, -1)} sx={{ minWidth: 44, minHeight: 44 }}>
                        <ArrowUpIcon fontSize="small" />
                      </IconButton>
                      <IconButton aria-label="Move down" disabled={saving || index === activeItems.length - 1} onClick={() => handleMove(index, 1)} sx={{ minWidth: 44, minHeight: 44 }}>
                        <ArrowDownIcon fontSize="small" />
                      </IconButton>
                      <IconButton aria-label="Remove property" disabled={saving} onClick={() => handleRemove(item)} color="error" sx={{ minWidth: 44, minHeight: 44 }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          )}
        </Box>

        <Paper
          square
          elevation={10}
          sx={{
            position: "fixed",
            bottom: 0,
            right: 0,
            width: { xs: "100%", sm: 480 },
            p: 1.5,
            pb: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          {pendingSmsConfirmation && (
            <Alert
              severity="info"
              sx={{ mb: 1 }}
              action={
                <Stack direction="row" spacing={0.5}>
                  <Button
                    size="small"
                    onClick={() => setPendingSmsConfirmation(null)}
                    disabled={confirmingSms}
                  >
                    Didn't send
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleConfirmSmsSent}
                    disabled={confirmingSms}
                  >
                    {confirmingSms ? "Saving…" : "Yes, mark sent"}
                  </Button>
                </Stack>
              }
            >
              Did the text send?
            </Alert>
          )}
          <Button
            fullWidth
            variant="contained"
            color="primary"
            startIcon={<TextsmsIcon />}
            onClick={handleText}
            disabled={!client?.phone || !detail?.shortlist.public_token || activeItems.length === 0}
            sx={{ minHeight: 48, mb: 1, borderRadius: 2.5, textTransform: "none", fontWeight: 700 }}
          >
            {client ? `Text it to ${client.first_name}` : "Text shortlist"}
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={() => setOpen(false)}
            sx={{ minHeight: 48, borderRadius: 2.5, textTransform: "none", fontWeight: 700 }}
          >
            Done · keep searching
          </Button>
        </Paper>
      </Drawer>
    </>
  );
};

// A client switch owns a fresh composer. In-flight work keeps its original
// client, but cannot reopen or populate the next client's shortlist drawer.
const ShortlistComposer: React.FC<ShortlistComposerProps> = (props) => (
  <ClientShortlistComposer key={props.client?.id ?? "no-client"} {...props} />
);

export default ShortlistComposer;
